<script setup>
import { ref, computed, watch } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// Safelist for UnoCSS scanner:
// i-lucide-terminal i-lucide-trash-2 i-lucide-check-circle-2 i-lucide-x-circle i-lucide-chevron-up i-lucide-chevron-down i-lucide-brain i-lucide-bot

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
  logs: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['clear-logs']);

const expandedIndex = ref(null);

// Tool icon map for known tool names
const toolIconMap = {
  search_documentation: '📄',
  search_past_conversations: '💬',
  cetakers_flow_get_products: '🛍️',
  cetakers_flow_get_spk: '📋',
  cetakers_flow_get_invoices: '🧾',
  cetakers_flow_get_shipping_tracking: '🚚',
  cetakers_flow_get_customers: '👤',
  cetakers_flow_get_sales_channels: '📡',
  cetakers_flow_create_order: '🛒',
  create_private_note: '📝',
};

const toolLabel = name => {
  const labels = {
    search_documentation: 'Cari FAQ',
    search_past_conversations: 'Cek Percakapan',
    cetakers_flow_get_products: 'Katalog Produk',
    cetakers_flow_get_spk: 'Cek SPK',
    cetakers_flow_get_invoices: 'Cek Invoice',
    cetakers_flow_get_shipping_tracking: 'Lacak Kiriman',
    cetakers_flow_get_customers: 'Data Pelanggan',
    cetakers_flow_get_sales_channels: 'Sales Channel',
    cetakers_flow_create_order: 'Buat Order',
    create_private_note: 'Buat Catatan',
  };
  return labels[name] || name;
};

const formatTime = iso => {
  if (!iso) return '--:--:--';
  const d = new Date(iso);
  return d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatDuration = ms => {
  if (!ms && ms !== 0) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const toggleExpand = index => {
  expandedIndex.value = expandedIndex.value === index ? null : index;
};

const totalRequests = computed(() => props.logs.length);
const successCount = computed(() => props.logs.filter(l => l.status === 'success').length);
const errorCount = computed(() => props.logs.filter(l => l.status === 'error').length);
const avgDuration = computed(() => {
  if (!props.logs.length) return '-';
  const total = props.logs.reduce((sum, l) => sum + (l.durationMs || 0), 0);
  return formatDuration(Math.round(total / props.logs.length));
});
</script>

<template>
  <div class="flex flex-col h-full rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-11 overflow-hidden">
    <!-- Header -->
    <div class="px-4 pt-5 pb-3 border-b border-n-weak flex-shrink-0">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2.5">
          <div class="flex items-center justify-center size-7 rounded-lg bg-woot-50 text-woot-600 border border-woot-200/60 shadow-xs">
            <Icon icon="i-lucide-terminal" class="size-4 text-woot-600" />
          </div>
          <div>
            <div class="flex items-center gap-1.5">
              <h3 class="text-sm font-semibold text-n-slate-12">Debug Log</h3>
              <span class="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-woot-100 text-woot-700 tracking-wide uppercase">Enterprise</span>
            </div>
          </div>
        </div>
        <button
          v-if="logs.length > 0"
          class="text-xs text-n-slate-9 hover:text-r-9 transition-colors px-2 py-1 rounded hover:bg-r-2 flex items-center gap-1"
          @click="emit('clear-logs')"
        >
          <Icon icon="i-lucide-trash-2" class="size-3" />
          <span>Hapus Log</span>
        </button>
      </div>

      <!-- Summary stats -->
      <div class="grid grid-cols-4 gap-1.5">
        <div class="bg-n-alpha-2 rounded-lg px-2 py-1.5 text-center">
          <div class="text-base font-bold text-n-slate-12">{{ totalRequests }}</div>
          <div class="text-[10px] text-n-slate-9 leading-tight">Total</div>
        </div>
        <div class="bg-woot-50 rounded-lg px-2 py-1.5 text-center">
          <div class="text-base font-bold text-woot-600">{{ successCount }}</div>
          <div class="text-[10px] text-woot-500 leading-tight">Sukses</div>
        </div>
        <div class="bg-r-2 rounded-lg px-2 py-1.5 text-center">
          <div class="text-base font-bold text-r-9">{{ errorCount }}</div>
          <div class="text-[10px] text-r-8 leading-tight">Error</div>
        </div>
        <div class="bg-n-alpha-2 rounded-lg px-2 py-1.5 text-center">
          <div class="text-base font-bold text-n-slate-12">{{ avgDuration }}</div>
          <div class="text-[10px] text-n-slate-9 leading-tight">Rata-rata</div>
        </div>
      </div>
    </div>

    <!-- Log entries -->
    <div class="flex-1 overflow-y-auto p-3 space-y-2">
      <!-- Empty state -->
      <div v-if="logs.length === 0" class="flex flex-col items-center justify-center h-full text-center gap-3 py-8">
        <div class="text-3xl opacity-30">📋</div>
        <div>
          <p class="text-xs font-medium text-n-slate-11">Belum ada log</p>
          <p class="text-[11px] text-n-slate-9 mt-1">Kirim pesan di area pengujian untuk mulai merekam log</p>
        </div>
      </div>

      <!-- Log list (newest first) -->
      <div
        v-for="(log, index) in [...logs].reverse()"
        :key="index"
        class="border rounded-lg overflow-hidden transition-all"
        :class="log.status === 'error' ? 'border-r-4 bg-r-2' : 'border-n-weak bg-n-alpha-1'"
      >
        <!-- Log header row -->
        <div
          class="flex items-start gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-n-alpha-2 transition-colors"
          @click="toggleExpand(index)"
        >
          <!-- Status icon -->
          <Icon
            :icon="log.status === 'success' ? 'i-lucide-circle-check-big' : 'i-lucide-circle-x'"
            class="size-4 mt-0.5 flex-shrink-0"
            :class="log.status === 'success' ? 'text-woot-500' : 'text-r-9'"
          />

          <!-- Main info -->
          <div class="flex-1 min-w-0">
            <!-- Query text -->
            <p class="text-xs font-medium text-n-slate-12 truncate leading-tight">
              "{{ log.query }}"
            </p>
            <!-- Metadata row -->
            <div class="flex items-center gap-2 mt-1 flex-wrap">
              <span class="text-[10px] text-n-slate-9 font-mono">{{ formatTime(log.timestamp) }}</span>
              <span
                class="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                :class="log.status === 'success' ? 'bg-woot-100 text-woot-700' : 'bg-r-3 text-r-9'"
              >
                {{ formatDuration(log.durationMs || log.duration_ms) }}
              </span>
            </div>

            <!-- Tool chips -->
            <div v-if="(log.toolCallsMade || log.tool_calls_made) && (log.toolCallsMade || log.tool_calls_made).length > 0" class="flex flex-wrap gap-1 mt-1.5">
              <span
                v-for="tool in (log.toolCallsMade || log.tool_calls_made)"
                :key="tool"
                class="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-3 text-n-slate-10 border border-n-weak font-medium"
              >
                <span>{{ toolIconMap[tool] || '🔧' }}</span>
                <span>{{ toolLabel(tool) }}</span>
              </span>
            </div>
            <div v-else-if="log.status === 'success'" class="mt-1">
              <span class="text-[10px] text-n-slate-9 italic">Tidak ada tool dipanggil</span>
            </div>
          </div>

          <!-- Expand toggle -->
          <Icon
            :icon="expandedIndex === index ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
            class="size-3.5 text-n-slate-9 flex-shrink-0 mt-0.5"
          />
        </div>

        <!-- Expanded: response & reasoning preview -->
        <div
          v-if="expandedIndex === index"
          class="border-t px-3 py-2.5 space-y-2.5"
          :class="log.status === 'error' ? 'border-r-4 bg-r-1' : 'border-n-weak bg-n-solid-1'"
        >
          <div v-if="log.reasoning">
            <div class="flex items-center gap-1 mb-1 text-woot-600">
              <Icon icon="i-lucide-brain" class="size-3.5 text-woot-600" />
              <p class="text-[10px] font-semibold uppercase tracking-wider">Alasan / Reasoning AI</p>
            </div>
            <p class="text-xs text-n-slate-11 bg-woot-50/50 border border-woot-200/40 p-2 rounded-md italic leading-relaxed break-words">
              {{ log.reasoning }}
            </p>
          </div>
          <div>
            <div class="flex items-center gap-1 mb-1 text-n-slate-9">
              <Icon icon="i-lucide-bot" class="size-3.5 text-n-slate-9" />
              <p class="text-[10px] font-semibold uppercase tracking-wider">Respons AI</p>
            </div>
            <p
              class="text-xs text-n-slate-11 leading-relaxed break-words"
              :class="log.status === 'error' ? 'text-r-9' : ''"
            >
              {{ log.response || log.error || '(tidak ada konten)' }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="px-4 py-2 border-t border-n-weak flex-shrink-0">
      <p class="text-[10px] text-n-slate-8 text-center">
        Log tersimpan secara lokal • Sesi ini: {{ totalRequests }} permintaan
      </p>
    </div>
  </div>
</template>
